import type { ThemeOptions } from '@mui/material/styles';

/**
 * Hex → token map (most common literals outside src/shared/theme):
 *
 * Primary / brand
 *   #1875ff, #5d87ff, #005ee9, #4570ea, #1976d2, #2196f3, #2563eb, #60a5fa
 *     → palette.primary.main / .dark / .light / info.main
 *   #ecf2ff, #eff3ff, #e0e7ff, #dbeafe, #eef2ff → palette.primary.light / surface.raised
 *   #1875ff0d, #1875ff4d, #1875ff80 → alpha(primary.main, n) / primary.alphaChannel
 *
 * Text
 *   #2a3547, #1f2937, #111827, #0b111d, #0b1020, #101828 → text.primary / extendedGrey.darker
 *   #7c8fac, #6b7280, #64748b, #9ca3af, #94a3b8 → text.secondary / extendedGrey.neutral
 *   #5a6a85, #475467, #374151, #334155 → extendedGrey.dark
 *
 * Surfaces
 *   #ffffff, #fff → background.default / surface.page / surface.card
 *   #f2f6fa, #f6f8fc, #f8fbff, #fafbfc, #f9fafb, #f3f4f6, #f8fafc, #f9f9f9
 *     → surface.sunken / surface.raised / grey.100 / extendedGrey.lightest
 *
 * Borders
 *   #dfe5ef, #eaeff4, #e5eaef, #e6eaf0, #eef2f7, #e5e7eb, #d0d5dd, #d1d5db, #cbd5e1, #e2e8f0
 *     → border.default / border.subtle / border.strong / divider / extendedGrey.neutralLight
 *
 * Status
 *   #13deb9, #22c55e, #10b981, #059669, #dcfce7, #14532d → status.success / success.*
 *   #ffae1f, #f59e0b, #fbbf24, #fef3c7, #fef6ee → status.warning / warning.*
 *   #fa896b, #ef4444, #dc2626, #f04438, #fee2e2, #fef2f2, #b42318, #7f1d1d → status.danger / error.*
 *   #539bff, #49beff → status.info / info.* / secondary.main
 *
 * Charts → palette.chart[]
 * Canvas (handoff / task flow) → palette.canvas.*
 */

const baselightTheme: ThemeOptions = {
  direction: 'ltr',
  palette: {
    mode: 'light',
    primary: {
      main: '#1875FF',
      light: '#ECF2FF',
      dark: '#4570EA',
      alphaChannel: '#1875FF0D',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#49BEFF',
      light: '#E8F7FF',
      dark: '#23afdb',
      contrastText: '#ffffff',
    },
    success: {
      main: '#13DEB9',
      light: '#D2FADF',
      dark: '#003718',
      contrastText: '#ffffff',
    },
    info: {
      main: '#539BFF',
      light: '#EBF3FE',
      dark: '#1682d4',
      contrastText: '#ffffff',
    },
    error: {
      main: '#FA896B',
      light: '#FDEDE8',
      dark: '#f3704d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FFAE1F',
      light: '#FEF5E5',
      dark: '#ae8e59',
      contrastText: '#ffffff',
    },
    purple: {
      A50: '#EBF3FE',
      A100: '#6610f2',
      A200: '#557fb9',
    },
    extendedGrey: {
      lightest: '#F2F6FA',
      lighter: '#F6F8FC',
      light: '#EAEFF4',
      neutralLight: '#DFE5EF',
      neutral: '#7C8FAC',
      dark: '#5A6A85',
      darker: '#2A3547',
      darkest: '#111827',
    },
    grey: {
      100: '#F2F6FA',
      A100: '#F6F8FC',
      200: '#EAEFF4',
      300: '#DFE5EF',
      400: '#7C8FAC',
      500: '#5A6A85',
      600: '#2A3547',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    text: {
      primary: '#2A3547',
      secondary: '#5A6A85',
    },
    action: {
      disabledBackground: 'rgba(73,82,88,0.12)',
      hoverOpacity: 0.02,
      hover: '#f6f9fc',
    },
    divider: '#e5eaef',
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    surface: {
      page: '#ffffff',
      card: '#ffffff',
      raised: '#F6F8FC',
      sunken: '#F2F6FA',
      overlay: 'rgba(15, 23, 42, 0.45)',
    },
    border: {
      subtle: '#EEF2F7',
      default: '#DFE5EF',
      strong: '#D0D5DD',
    },
    status: {
      success: {
        foreground: '#14532D',
        background: '#DCFCE7',
        border: '#86EFAC',
      },
      warning: {
        foreground: '#78350F',
        background: '#FEF3C7',
        border: '#FCD34D',
      },
      danger: {
        foreground: '#B42318',
        background: '#FEE2E2',
        border: '#FECACA',
      },
      info: {
        foreground: '#0C4A6E',
        background: '#E0F2FE',
        border: '#7DD3FC',
      },
      neutral: {
        foreground: '#374151',
        background: '#F3F4F6',
        border: '#E5E7EB',
      },
    },
    chart: [
      '#4F46E5',
      '#7C3AED',
      '#2563EB',
      '#0891B2',
      '#059669',
      '#65A30D',
      '#CA8A04',
      '#EA580C',
      '#DC2626',
      '#DB2777',
      '#9333EA',
      '#0D9488',
      '#94A3B8',
    ],
    canvas: {
      background: '#F8FAFC',
      nodeBackground: '#FFFFFF',
      nodeBorder: '#DFE5EF',
      nodeSelected: '#1875FF',
      edge: '#94A3B8',
      handle: '#1875FF',
      selection: '#1875FF4D',
      grid: '#E2E8F0',
    },
  },
};

const baseDarkTheme: ThemeOptions = {
  direction: 'ltr',
  palette: {
    mode: 'dark',
    primary: {
      main: '#5D87FF',
      light: '#ECF2FF',
      dark: '#4570EA',
      alphaChannel: '#5D87FF1A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#777e89',
      light: '#1C455D',
      dark: '#173f98',
      contrastText: '#ffffff',
    },
    success: {
      main: '#13DEB9',
      light: '#1B3C48',
      dark: '#02b3a9',
      contrastText: '#ffffff',
    },
    info: {
      main: '#539BFF',
      light: '#223662',
      dark: '#1682d4',
      contrastText: '#ffffff',
    },
    error: {
      main: '#FA896B',
      light: '#4B313D',
      dark: '#f3704d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FFAE1F',
      light: '#4D3A2A',
      dark: '#ae8e59',
      contrastText: '#ffffff',
    },
    purple: {
      A50: '#EBF3FE',
      A100: '#6610f2',
      A200: '#557fb9',
    },
    extendedGrey: {
      lightest: '#2A3547',
      lighter: '#333F55',
      light: '#465670',
      neutralLight: '#7C8FAC',
      neutral: '#A7B4C8',
      dark: '#EAEFF4',
      darker: '#F2F6FA',
      darkest: '#FFFFFF',
    },
    grey: {
      100: '#333F55',
      A100: '#F6F8FC',
      200: '#465670',
      300: '#7C8FAC',
      400: '#DFE5EF',
      500: '#EAEFF4',
      600: '#F2F6FA',
      700: '#F3F4F6',
      800: '#F9FAFB',
      900: '#FFFFFF',
    },
    text: {
      primary: '#EAEFF4',
      secondary: '#B2C0D3',
    },
    action: {
      disabledBackground: 'rgba(73,82,88,0.12)',
      hoverOpacity: 0.02,
      hover: '#333F55',
    },
    divider: '#333F55',
    background: {
      default: '#171c23',
      paper: '#1f2733',
    },
    surface: {
      page: '#171c23',
      card: '#1f2733',
      raised: '#333F55',
      sunken: '#12161d',
      overlay: 'rgba(0, 0, 0, 0.6)',
    },
    border: {
      subtle: '#2A3547',
      default: '#333F55',
      strong: '#465670',
    },
    status: {
      success: {
        foreground: '#86EFAC',
        background: '#14532D',
        border: '#166534',
      },
      warning: {
        foreground: '#FCD34D',
        background: '#78350F',
        border: '#92400E',
      },
      danger: {
        foreground: '#FECACA',
        background: '#7F1D1D',
        border: '#991B1B',
      },
      info: {
        foreground: '#7DD3FC',
        background: '#0C4A6E',
        border: '#075985',
      },
      neutral: {
        foreground: '#E5E7EB',
        background: '#374151',
        border: '#4B5563',
      },
    },
    chart: [
      '#818CF8',
      '#A78BFA',
      '#60A5FA',
      '#22D3EE',
      '#34D399',
      '#A3E635',
      '#FBBF24',
      '#FB923C',
      '#F87171',
      '#F472B6',
      '#C084FC',
      '#2DD4BF',
      '#94A3B8',
    ],
    canvas: {
      background: '#0F172A',
      nodeBackground: '#1E293B',
      nodeBorder: '#334155',
      nodeSelected: '#5D87FF',
      edge: '#64748B',
      handle: '#5D87FF',
      selection: '#5D87FF66',
      grid: '#1E293B',
    },
  },
};

export { baseDarkTheme, baselightTheme };
